import type { ReactNode } from 'react';
import { BackButton, Button, TypePill } from '@wundo/ui';
import { formatElapsed } from './practiceFormat.ts';
import { m } from '@/paraglide/messages.js';

/** Before the conversation starts, while there is one to end, or once it is over. */
export type PracticeSessionHeaderState = 'before' | 'running' | 'over';

export interface PracticeSessionHeaderProps {
  /** See `PracticeSessionExperience`. */
  brand: ReactNode | undefined;
  /** Who the user is talking to, and their role. */
  title: string;
  typeLabel: string | null | undefined;
  state: PracticeSessionHeaderState;
  linkOnly: boolean;
  elapsed: number;
  ending: boolean;
  onAbandon: () => void;
  onEnd: () => void;
}

/**
 * The row over a practice conversation: whose it is, the clock and the way to
 * end it. Once it is over only the logo stays, because a link visitor has
 * nothing else saying whose page this is.
 */
export function PracticeSessionHeader({
  brand,
  title,
  typeLabel,
  state,
  linkOnly,
  elapsed,
  ending,
  onAbandon,
  onEnd,
}: PracticeSessionHeaderProps) {
  if (state === 'over' && brand === undefined) {
    return null;
  }

  return (
    <header className='shrink-0 border-b border-line bg-surface px-5 py-4.5'>
      <div className='mx-auto flex w-full max-w-240 flex-wrap items-center gap-x-3 gap-y-2'>
        {brand}
        {state !== 'over' && (
          <>
            {brand !== undefined && (
              <span aria-hidden='true' className='h-5 w-px bg-line' />
            )}
            <h1 className='min-w-0 truncate text-md font-semibold text-ink'>{title}</h1>
            {typeLabel != null && <TypePill type='none'>{typeLabel}</TypePill>}

            <div className='ml-auto flex items-center gap-4'>
              {/*
               * Before the start there is nothing to end, but there is always a
               * way out: this screen covers everything under it.
               */}
              {state === 'running' ? (
                <>
                  <span
                    aria-label={m.practice_session_elapsed()}
                    className='text-base font-medium tracking-wide text-ink-secondary tabular-nums'
                  >
                    {formatElapsed(elapsed)}
                  </span>
                  <Button disabled={ending} onClick={onEnd}>
                    {m.practice_session_end()}
                  </Button>
                </>
              ) : (
                <PracticeExitButton linkOnly={linkOnly} onClick={onAbandon} />
              )}
            </div>
          </>
        )}
      </div>
    </header>
  );
}

/* A link visitor has no Oefenen to go back to, so they close. */
function PracticeExitButton({
  linkOnly,
  onClick,
}: {
  linkOnly: boolean;
  onClick: () => void;
}) {
  if (linkOnly) {
    return (
      <Button variant='ghost' onClick={onClick}>
        {m.practice_close()}
      </Button>
    );
  }
  return <BackButton onClick={onClick}>{m.practice_back()}</BackButton>;
}
